// In src/components/DistributionListManager.js

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import {
  collection, query, where, onSnapshot, doc,
  writeBatch, serverTimestamp, orderBy, deleteDoc,
} from 'firebase/firestore';
import {
  Box, Button, TextField, Typography, Paper, List,
  ListItem, ListItemText, IconButton, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, CircularProgress, Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

function DistributionListManager() {
  const { userProfile } = useAuth();
  const [lists, setLists] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentList, setCurrentList] = useState(null);
  const [listName, setListName] = useState('');
  const [listDescription, setListDescription] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userProfile?.churchId) return;

    // Fetch all active users in the church
    const usersQuery = query(
      collection(db, 'users'),
      where('churchId', '==', userProfile.churchId),
      where('status', '==', 'active'),
      orderBy('lastName', 'asc'),
    );
    const unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
      setAllUsers(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    // Fetch all distribution lists for the church
    const listsQuery = query(
      collection(db, `churches/${userProfile.churchId}/distributionLists`),
      orderBy('name', 'asc'),
    );
    const unsubscribeLists = onSnapshot(listsQuery, (snapshot) => {
      setLists(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return () => {
      unsubscribeUsers();
      unsubscribeLists();
    };
  }, [userProfile?.churchId]);

  const sortedUsers = useMemo(() => {
    return [...allUsers].sort((a, b) =>
      a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName)
    );
  }, [allUsers]);

  const handleOpenModal = (list = null) => {
    setCurrentList(list);
    setListName(list ? list.name : '');
    setListDescription(list ? list.description : '');
    setSelectedMembers(list ? list.memberUids || [] : []);
    setError('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentList(null);
  };

  const handleMemberToggle = (userId) => {
    setSelectedMembers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSaveList = async () => {
    if (!listName.trim()) {
      setError("List name cannot be empty.");
      return;
    }
    setError('');
    const batch = writeBatch(db);
    const listRef = currentList
      ? doc(db, `churches/${userProfile.churchId}/distributionLists`, currentList.id)
      : doc(collection(db, `churches/${userProfile.churchId}/distributionLists`));

    batch.set(listRef, {
      name: listName,
      description: listDescription,
      memberUids: selectedMembers,
      updatedAt: serverTimestamp(),
      ...( !currentList && { createdAt: serverTimestamp() } ),
    }, { merge: true });

    try {
      await batch.commit();
      handleCloseModal();
    } catch (err) {
      console.error("Error saving distribution list:", err);
      setError("Failed to save the list. Please try again.");
    }
  };

  const handleDeleteList = async (listId) => {
    if (window.confirm("Are you sure you want to delete this list? This action cannot be undone.")) {
      try {
        await deleteDoc(doc(db, `churches/${userProfile.churchId}/distributionLists`, listId));
      } catch (err) {
        console.error("Error deleting list:", err);
        alert("Failed to delete the list.");
      }
    }
  };


  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Distribution Lists</Typography>
        <Button variant="contained" onClick={() => handleOpenModal()}>
          Create New List
        </Button>
      </Box>
      <Paper>
        <List>
          {lists.map((list, index) => (
            <React.Fragment key={list.id}>
              <ListItem
                secondaryAction={
                  <>
                    <IconButton edge="end" aria-label="edit" onClick={() => handleOpenModal(list)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteList(list.id)} sx={{ ml: 1 }}>
                      <DeleteIcon />
                    </IconButton>
                  </>
                }
              >
                <ListItemText
                  primary={list.name}
                  secondary={`${list.memberUids?.length || 0} members - ${list.description || 'No description'}`}
                />
              </ListItem>
              {index < lists.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{currentList ? 'Edit List' : 'Create New List'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            margin="dense"
            label="List Name"
            fullWidth
            variant="outlined"
            value={listName}
            onChange={(e) => setListName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            variant="outlined"
            value={listDescription}
            onChange={(e) => setListDescription(e.target.value)}
          />
          <Typography sx={{ mt: 2, mb: 1 }}>Members ({selectedMembers.length})</Typography>
          <Paper variant="outlined" sx={{ maxHeight: '40vh', overflow: 'auto' }}>
            <List dense>
              {sortedUsers.map((user) => (
                <ListItem key={user.id} dense button onClick={() => handleMemberToggle(user.id)}>
                  <Checkbox
                    edge="start"
                    checked={selectedMembers.includes(user.id)}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText primary={`${user.firstName} ${user.lastName}`} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSaveList} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DistributionListManager;